import styled from 'styled-components'
import { v1 as color } from '../color'
import { media } from '../mediaQuery'

// HeaderRow should be used for headers,
// title bars, navigation bars, and heros
// that need to be above body content, but
// expand the full page width.
export const HeaderRow = styled.div`
  display: table-row;
  width: 100%;
  height: 1px;
`

// BodyRow should be used for the main body
// of the app, excluding hero bars and things
// that HeaderRow is better suited for. This
// is the part of the page that will expand
// downward to keep the footer at the bottom.
export const BodyRow = styled.div`
  content: '';
  display: table-row;
  height: 100%;
`

// Like HeaderRow, FooterRow should be used for
// full width footer content. Due to the nature
// of the BodyRow, FooterRow will be stuck to the
// bottom of the page no matter how much content
// fills BodyRow.
export const FooterRow = styled.div`
  display: table-row;
`

// ----------

// BodyContent should not have any padding
// because we will likely want to have its
// content bleed all the way to the edges.
export const BodyContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;

  ${media.desktopAndUp`
    width: 90%;
    max-width:  75rem;
    margin: 0 auto;
  `}

  ${media.laptop`
    width: 90%;
    margin: auto;
  `}

  ${media.tabletAndBelow`
    width: 100%;
    flex-direction: column;
    justify-content: flex-start;
  `}
`

export const HeaderContent = BodyContent
export const FooterContent = BodyContent

// ----------

// all column components will inherit this class
export const BodyColumn = styled.div`
  height: auto;

  // tablet and below is flex-direction: column;
  // disable flex-grow to have content stack
  // without extra whitespace.
  ${media.tabletAndBelow`
    flex-grow: 0;
    width: 100%;
  `}
`

// for when we only need a single column.
// Dashboard/NoAcct and NotFound pages use this.
export const FullColumn = styled(BodyColumn)`
  width: 100%;
`

// NavColumn is used on on pages like Account, Doctors
// and Expenses.
export const NavColumn = styled(BodyColumn)`
  flex-basis: 0;
  border-right: solid 1px ${color.SILVER};
  box-shadow: 0.625rem 0 ${color.GALLERY};
  z-index: 1;
  height: 100%;

  ${p =>
    p.withBorder &&
    `
    border-right: solid 1px ${color.SILVER};
    box-shadow: 0.625rem 0 ${color.GALLERY};
  `}

  ${media.desktopAndUp`
    flex-grow: .809;
  `}
  ${media.laptop`
    flex-grow: .809;
  `}
  ${media.tabletAndBelow`
    display: none;
  `}
`

export const MainColumn = styled(BodyColumn)`
  flex-basis: 0;

  & > h1:first-of-type,
  & > h2:first-of-type {
    margin-top: 0;
  }

  ${p =>
    p.busy &&
    `
    opacity: 0;
    display: none;
  `}

  ${media.desktopAndUp`
    flex-grow: 3.236;
    margin: 1.875rem 1.875rem 1.875rem 0;
  `}
  ${media.laptop`
    flex-grow: 3.236;
    margin: 1.875rem 1.875rem 1.875rem 0;
  `}
  ${media.tabletAndBelow`
    width: 100%;
    padding: 1.875rem 1.875rem 0;
    margin-bottom: 1.875rem;
  `}
  ${media.mobileAndBelow`
    padding: 1.25rem 1.25rem 0;
    margin-bottom: 1.25rem;
  `}
`

export const SideColumn = styled(BodyColumn)`
  flex-basis: 0;

  ${media.desktopAndUp`
    flex-grow: 1.618;
    margin: 1.875rem 0;
  `}
  ${media.laptop`
    flex-grow: 1.618;
    margin: 1.875rem 0;
  `}
  ${media.tabletAndBelow`
    width: 100%;
    padding: 0 1.875rem;
    margin-bottom: 1.875rem;
  `}
  ${media.mobileAndBelow`
    padding: 0 1.25rem;
    margin-bottom: 1.25rem;
  `}
`

// ---------

export const Section = styled.div`
  padding: 1.875rem;
  border: solid 1px ${color.SILVER};
  border-radius: 0.3125rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.25);
  background: #fff;
  position: relative;
  counter-increment: title;

  h2,
  h3 {
    margin-top: 0;
    padding-top: 0;
    margin-bottom: 0.625rem;
  }

  ${media.mobileAndBelow`
    padding: 1.875rem 1.875rem 1.875rem;
    box-shadow: 0 -1px 0 #aaa;
    border: none;
    border-top: 10px solid #eee;
    border-radius: 0;
    margin-bottom: 0px;

    ${p =>
      p.first &&
      `
      box-shadow: none;
      border-top: none;
      padding-top: 0;
    `}
  `}
`

export const SectionTitle = styled.div`
  margin: 0 0 1.25rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  cursor: ${p => (p.clickable ? 'pointer' : 'auto')};

  h2,
  h3 {
    margin: 0;
    padding: 0;
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: baseline;

    ${p =>
      p.showNumber &&
      `
      &:before {
        padding-top: 0.0625rem;
        content: counter(title);
        color: ${color.MIDNIGHT};
        border: solid 1px ${color.SILVER};
        border-radius: 50%;
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;
        line-height: 0;
        margin-right: 0.625rem;

        // important to do max and min to ensure perfect circles,
        // just width or height is often not enough SID-3126
        max-width: 2.375rem;
        min-width: 2.375rem;
        min-height: 2.375rem;
        max-height: 2.375rem;
      }
    `}
  }
`

// ---------

export const gutterSize = '1.25rem'

export const Grid = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

/**
 * The negative margin at the end of each row is to offset the
 * padding of the columns that creates the gutter.
 */
export const GridRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-right: -${gutterSize};
`

/**
 * GridCell should be given an array of 4 sizes that will
 * correspond to the different responsive categories. If
 * no size is given, the cell will be full width (12) at
 * all screen sizes.
 *
 * 0: mobile
 * 1: tablet
 * 2: laptop
 * 3: desktop
 *
 * <GridColumn size={[12, 12, 12, 12]} >
 */
export const GridCell = styled.div`
  flex-basis: 100%;

  // we do bottom gutters on columns because sometimes they wrap
  // within a row and putting gutters on the rows mean the wrapped
  // columns won't be spaced correctly. Half-size tightens things up.
  margin-bottom: ${gutterSize / 2};

  // this padding is to provide gutter space between columns.
  // using padding instead of margins works lets the negative
  // margin of the row take up the slack and display correctly.
  padding-right: ${gutterSize};

  ${media.desktopAndUp`
    flex-basis: ${p => (p.size ? p.size[3] : 12) / 0.12}%;
  `}

  ${media.laptop`
    flex-basis: ${p => (p.size ? p.size[2] : 12) / 0.12}%;
  `}

  ${media.tablet`
    flex-basis: ${p => (p.size ? p.size[1] : 12) / 0.12}%;
  `}

  ${media.mobileAndBelow`
    flex-basis: ${p => (p.size ? p.size[0] : 12) / 0.12}%;
  `}
`

/**
 * input fields are "special" in that they don't manage their own
 * spacing for their label or validation errors. as a result, the
 * grid needs to be responsible for providing this space, otherwise
 * labels and errors will run into one another.
 *
 * instead of affecting all uses of the grid, this column
 */
export const InputCell = styled(GridCell)`
  padding-top: 0.75rem;
  padding-bottom: 1.5625rem;
  ${p =>
    p.bottomMargin &&
    `
    margin-bottom: 1.5625rem;
  `}
`
